import type {
  DomainLoanApplicationDetails,
  DomainLoanApplicationLoanOfficer,
  DomainLoanApplicationSalesRep,
  DomainLoanApplicationTeamMember,
} from "@porchpass/api-client";
import { Button } from "@porchpass/ui";
import { useState } from "react";
import { LogoCircle } from "src/assets/icons";
import ConfirmSalesRepDialog from "src/components/ConfirmSalesRepDialog";
import SalesRepSelector from "src/components/dropdowns/SalesRepSelector";
import Monogram from "src/components/Monogram";
import { useDealerUserInfo } from "src/context/DealerUserInfoContext";
import { useDealerEmployees } from "src/hooks/useDealerEmployees";
import { formatPhoneNumber } from "src/utils";
import { useApplication } from "../ApplicationContext";

interface TeamDetailsRowProps {
  salesRep: DomainLoanApplicationSalesRep;
  teamMembers: DomainLoanApplicationTeamMember[];
  loanOfficer: DomainLoanApplicationLoanOfficer;
}

export default ({
  salesRep,
  teamMembers,
  loanOfficer,
}: TeamDetailsRowProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingEmployee, setPendingEmployee] = useState<{
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null>(null);
  const { dealerUserInfo } = useDealerUserInfo() || { dealerUserInfo: {} };
  const { updateApplicationDetails, refetchApplication } = useApplication();
  // Fetch employees only when dropdown is opened
  const { employees, isLoading: isEmployeesLoading } =
    useDealerEmployees(showMenu);

  // Check if user can modify sales rep
  const canModify =
    dealerUserInfo.is_admin || dealerUserInfo.role === "GENERAL_MANAGER";

  const salesRepText = salesRep.name || salesRep.email;
  const hasSalesRep = Boolean(salesRep.name || salesRep.email);
  const hasTeamMembers = teamMembers.length > 0;
  const hasLoanOfficer = Boolean(
    loanOfficer?.name || loanOfficer?.phone_number,
  );

  // Find the current sales rep's employee ID (if any)
  const currentSalesRepId =
    employees.find(
      (emp) =>
        (emp.first_name && emp.last_name
          ? `${emp.first_name} ${emp.last_name}`
          : emp.email) === salesRepText,
    )?.id || "";

  const handleSelectEmployee = (employee: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  }) => {
    setPendingEmployee(employee);
    setIsDialogOpen(true);
  };

  const handleConfirmSalesRep = async () => {
    if (!pendingEmployee) {
      return;
    }

    const salesRepName =
      pendingEmployee.first_name && pendingEmployee.last_name
        ? `${pendingEmployee.first_name} ${pendingEmployee.last_name}`
        : "";
    const salesRepEmail = pendingEmployee.email;

    setIsUpdating(true);
    try {
      await updateApplicationDetails({
        sales_rep_name: salesRepName,
        sales_rep_email: salesRepEmail,
      } as Partial<DomainLoanApplicationDetails>);
      await refetchApplication();
      setShowMenu(false);
      setIsDialogOpen(false);
      setPendingEmployee(null);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelDialog = (open: boolean) => {
    if (!open) {
      setIsDialogOpen(false);
      setPendingEmployee(null);
    }
  };

  return (
    <div>
      {hasSalesRep && (
        <SalesRepSelector
          open={showMenu}
          onOpenChange={setShowMenu}
          selectedSalesRepId={currentSalesRepId}
          onSelectEmployee={handleSelectEmployee}
          canModify={canModify}
          isUpdating={isUpdating}
          employees={employees}
          isLoading={isEmployeesLoading}
        >
          <Button
            variant="ghost"
            disabled={!canModify}
            className="w-full border px-2.5 pl-4 "
            innerClassName="flex flex-row items-center justify-between w-full gap-4"
          >
            <div>Sales rep</div>
            <div className="flex flex-row items-center gap-1 font-medium text-sm text-black max-w-[245px]">
              <span className="overflow-hidden text-ellipsis whitespace-nowrap inline-block capitalize">
                {salesRepText}
              </span>
              <Monogram size={24}>{(salesRepText ?? "?").charAt(0)}</Monogram>
            </div>
          </Button>
        </SalesRepSelector>
      )}
      {hasSalesRep && (hasTeamMembers || hasLoanOfficer) && (
        <div className="border-b border-[#e4e7ec] my-4" />
      )}
      {hasTeamMembers && (
        <div className="flex flex-row items-center justify-between w-full gap-4 text-xs text-[#4a5466]">
          <div className="flex flex-row items-center gap-2">
            <LogoCircle /> Team members
          </div>
          <div className="flex flex-row items-center gap-1 font-medium text-sm text-black">
            {teamMembers.map((person) => (
              <Monogram size={24} key={person.email}>
                {(person.first_name ?? person.email ?? "?").charAt(0)}
              </Monogram>
            ))}
          </div>
        </div>
      )}
      {hasTeamMembers && hasLoanOfficer && (
        <div className="border-b border-[#e4e7ec] my-4" />
      )}
      {hasLoanOfficer && (
        <div className="flex flex-row items-center justify-between w-full gap-4 text-xs text-[#4a5466]">
          <div className="flex flex-row items-center gap-2">
            <LogoCircle /> Loan officer
          </div>
          <div className="flex flex-col items-end gap-2">
            {loanOfficer.name && (
              <div className="flex items-center gap-1">
                <span className="overflow-hidden text-ellipsis whitespace-nowrap inline-block capitalize font-medium text-sm text-black">
                  {loanOfficer.name}
                </span>
                <Monogram size={24}>
                  {(loanOfficer.name || "?").charAt(0)}
                </Monogram>
              </div>
            )}
            {loanOfficer.phone_number && (
              <div className="text-xs font-normal text-[#6b778c] mr-1">
                {formatPhoneNumber(loanOfficer.phone_number)}
              </div>
            )}
          </div>
        </div>
      )}
      <ConfirmSalesRepDialog
        open={isDialogOpen}
        onOpenChange={handleCancelDialog}
        onConfirm={handleConfirmSalesRep}
      />
    </div>
  );
};
